//@flow
/* eslint-disable prefer-template, no-path-concat, import/unambiguous, import/no-commonjs */
/* eslint-disable camelcase */

import { mat3, vec3 } from "gl-matrix";
import Cube from "../shapers/cube";
import StencilMode from "../../gl/stencil_mode";
import type Painter from "../../render/painter";

export default function drawLercGrid(painter: Painter, sourceCache, layer, coords) {
    const context = painter.context;
    context.setStencilMode(StencilMode.disabled);
    context.setColorMode(painter.colorModeForRenderPass());

    const gl = context.gl;
    gl.enable(gl.DEPTH_TEST);

    const normalProgram = painter.useProgram("lerc-grid");
    context.program.set(normalProgram.program);
    // get paint options.
    const scale = layer.paint.get("scale") || 1.0;
    const density = layer.paint.get("density") || 4;
    const baseValue = layer.paint.get("baseValue") || 0;
    const min = layer.paint.get("min") || 0;
    const max = layer.paint.get("max") || 100000;
    let opacity = layer.paint.get("opacity");
    opacity = typeof opacity === "number" ? opacity : 1;
    if (opacity === 0) {
        return;
    }
    // loop all coords to render each tile.
    coords.forEach(coord => {
        const tile = sourceCache.getTile(coord);
        const { lerc } = tile;
        const _source = sourceCache.getSource();
        const { transform } = painter;
        const currentZoom = transform.coveringZoomLevel({
            roundZoom: _source.roundZoom,
            tileSize: _source.tileSize,
        });
        if (coord.canonical.z !== currentZoom) return;
        const posMatrix = painter.transform.calculatePosMatrix(
            coord.toUnwrapped(),
        );
        const cube = new Cube({ density, lerc, base: baseValue, min, max });

        const program = painter.useProgram("lerc-grid");
        painter.context.gl.uniformMatrix4fv(
            program.uniforms.u_matrix,
            false,
            posMatrix,
        );

        context.activeTexture.set(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, tile.texture);

        painter.context.gl.uniform1i(program.uniforms.u_image0, 0);

        const a_position = gl.getAttribLocation(normalProgram.program, "a_pos");
        const a_gridnormal = gl.getAttribLocation(
            normalProgram.program,
            "a_gridnormal",
        );

        const vertexBuffer = gl.createBuffer();
        const gridnormalBuffer = gl.createBuffer();

        const indexBuffer = gl.createBuffer();

        const vertices = cube.getVertices();
        const gridnormals = cube.getNormals();
        const indices = cube.getIndices();

        const FSIZE_VERTICE = vertices.BYTES_PER_ELEMENT;
        const FSIZE_NORMAL = gridnormals.BYTES_PER_ELEMENT;

        gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

        gl.vertexAttribPointer(
            a_position,
            3,
            gl.FLOAT,
            false,
            FSIZE_VERTICE * 3,
            0,
        );
        gl.enableVertexAttribArray(a_position);

        gl.bindBuffer(gl.ARRAY_BUFFER, gridnormalBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, gridnormals, gl.STATIC_DRAW);

        gl.vertexAttribPointer(
            a_gridnormal,
            3,
            gl.FLOAT,
            false,
            FSIZE_NORMAL * 3,
            0,
        );
        gl.enableVertexAttribArray(a_gridnormal);

        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
        const { light } = painter.style;
        const _lp = light.properties.get("position");
        const lightPos = [_lp.x, _lp.y, _lp.z];
        const lightMat = mat3.create();
        if (light.properties.get("anchor") === "viewport") {
            mat3.fromRotation(lightMat, -painter.transform.angle);
        }
        vec3.transformMat3(lightPos, lightPos, lightMat);

        const color = light.properties.get("color") || {
            r: 1.0,
            g: 1.0,
            b: 1.0,
        };

        gl.uniform3fv(program.uniforms.u_lightpos, lightPos);
        gl.uniform1f(program.uniforms.u_scale, scale);
        gl.uniform1f(
            program.uniforms.u_lightintensity,
            light.properties.get("intensity"),
        );
        gl.uniform3f(program.uniforms.u_lightcolor, color.r, color.g, color.b);

        gl.drawElements(gl.TRIANGLES, indices.length, gl.UNSIGNED_SHORT, 0);
    });
}
